import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react'
import { MedicalEventHeaderProps } from './MedicalEventHeader.model'

import { IconButton, TooltipWrapper } from '../../../../shared'
import { Space } from './Space'

import styles from './MedicalEventHeader.module.scss'
import { DATE_FORMAT_FRONT_SHORT, formatFr } from '../../../../../misc/date.utilities'
import { getFullName, isDoctor, isInInteropOrganization } from '../../../../../misc/user.utilities'
import { ConnectedUserContext } from '../../../../../misc/auth.utilities'
import { hasDoctorRole } from '../../../../../misc/roles.utilities'
import { PatientPageHeader } from '../../PatientPageNavBar/PatientPageHeader'
import {
  InteropActionsMenu,
  InteropDocumentsAction,
} from '../../PatientPageNavBar/PatientPageHeader/InteropActionsMenu'
import { useNavigate } from 'react-router-dom'
import { SearchMedicalEventDocumentsBottomPanel } from '../../../../shared/bottomPanel/SearchMedicalEventDocumentsBottomPanel'
import { useDownloadDocuments, usePrintDocuments } from '../../../../../hooks/queries/document'
import { DocumentInstanceListItem, SendableDocument } from '../../../../../model/DocumentInstance'
import {
  getRenderedPdfFileName,
  isRenderableFileExtension,
} from '../../../../../misc/files.utilities'
import { BasicRenderMode } from '../../../../../model/Document'
import { CustomFile } from '../../../../../model/File'
import { SendFileModal } from '../../../../medicalEvent'
import { isDefined } from '../../../../../misc/functions.utilities'
import { UrlQueryParams } from '../../../../../store/domain/medicalEvents'
import { useGetMailSubject, useTestMailConnection } from '../../../../../hooks/queries/mail'
import { MailEditor } from '../../../../mail'
import { getQueryParam } from '../../../../../misc/url.utilities'
import { Tooltip } from '@follow-health/fw_ui_library/dist/components/atoms/common/Tooltip'
import { useSyncDocumentsInterop } from '../../../../../hooks/queries/patients'
import { usePrintingContext } from '../../../../../core/hooks/usePrintingContext'
import { useDownloadingContext } from '../../../../../core/hooks/useDownloadingContext'

// Les documents interop passent par la même sélection que l'impression et le téléchargement.
type MedicalEventDocumentsAction = BasicRenderMode | 'interop'

const SELECTION_PANEL_TITLES: { [action in MedicalEventDocumentsAction]: string } = {
  print: 'Sélectionner les documents à imprimer',
  download: 'Sélectionner les documents à télécharger',
  interop: "Sélectionner les documents à envoyer à l'établissement",
}

const getSelectionMode = (
  panelState: boolean | MedicalEventDocumentsAction,
): MedicalEventDocumentsAction => (typeof panelState === 'boolean' ? 'print' : panelState)

interface InitialMailState {
  content?: string
  object?: string
  emails?: Array<string>
  documents?: Array<SendableDocument>
  fileIds?: Array<number>
}

export const MedicalEventHeader: FunctionComponent<MedicalEventHeaderProps> = ({
  patient,
  medicalEvent,
  userEnabledFeatures,
  editMedicalEvent,
  isOwner,
  onLock,
}) => {
  const [
    isOpenSearchMedicalEventDocumentsBottomPanel,
    setIsOpenSearchMedicalEventDocumentsBottomPanel,
  ] = useState<boolean | MedicalEventDocumentsAction>(false)
  const [showSendFileModal, setShowSendFileModal] = useState(
    !!getQueryParam(UrlQueryParams.SENDING),
  )

  const [initialMail, setInitialMail] = useState<InitialMailState>()

  const { loggedUser, currentUser } = useContext(ConnectedUserContext)
  const allowedToLock = loggedUser && isOwner && hasDoctorRole(loggedUser.roles)
  const navigate = useNavigate()
  const { print } = usePrintDocuments()
  const { isLoading: isPrinting } = usePrintingContext()
  const { download } = useDownloadDocuments()

  const { isLoading: isDownloading } = useDownloadingContext()

  const { mutate: syncDocumentsToInterop, isPending: isSyncingDocuments } =
    useSyncDocumentsInterop()
  const isInteropUser = isInInteropOrganization(currentUser)

  const { mutate: testMailConnection } = useTestMailConnection()
  const { mutateAsync: getMailSubject, isPending: isMailSubjectLoading } = useGetMailSubject()

  const tooltipContent = useMemo(() => {
    if (userEnabledFeatures?.mail) {
      if (userEnabledFeatures.mssSmtp && isDoctor(currentUser) && !currentUser.mailizEmailAddress) {
        return "Vous devez saisir votre adresse MSS Mailiz dans vos informations de compte afin d'accéder à votre boîte de réception dans Follow. Cliquez sur le bouton pour saisir une adresse email"
      }

      return !patient?.inseeNumber
        ? 'Numéro de sécurité sociale du patient manquant'
        : 'Le patient refuse tout partage de ses informations via la MSS'
    }
  }, [currentUser, patient?.inseeNumber, userEnabledFeatures])

  const handlePrint = useCallback(
    ({ documents, files }: { documents: DocumentInstanceListItem[]; files: CustomFile[] }) => {
      print({
        documents: documents,
        files: files,
      })
    },
    [print],
  )

  const handleDownload = useCallback(
    ({ documents, files }: { documents: DocumentInstanceListItem[]; files: CustomFile[] }) => {
      download({
        fileName: getRenderedPdfFileName(patient),
        documents: documents,
        files: files,
      })
    },
    [download, patient],
  )

  // Seuls les fichiers rendus par le back sont transmissibles, comme pour l'impression.
  const interopSyncableItems = useMemo(() => {
    const documents = medicalEvent?.documents ?? []
    const files = (medicalEvent?.files ?? []).filter(({ extension }) =>
      isRenderableFileExtension(extension),
    )

    return { documents, files, count: documents.length + files.length }
  }, [medicalEvent])

  const handleSyncDocumentsToInterop = useCallback(
    ({ documents, files }: { documents: DocumentInstanceListItem[]; files: CustomFile[] }) => {
      if (!patient || !medicalEvent) {
        return
      }

      syncDocumentsToInterop({
        serial: patient.serial,
        medicalEventId: medicalEvent.id,
        documents,
        files,
      })
    },
    [patient, medicalEvent, syncDocumentsToInterop],
  )

  const handleInteropSendClick = useCallback(() => {
    const { documents, files, count } = interopSyncableItems

    if (count === 1) {
      handleSyncDocumentsToInterop({ documents: [...documents], files: [...files] })
      return
    }

    setIsOpenSearchMedicalEventDocumentsBottomPanel('interop')
  }, [handleSyncDocumentsToInterop, interopSyncableItems])

  const interopDocumentsAction = useMemo<InteropDocumentsAction | undefined>(
    () =>
      isInteropUser && medicalEvent
        ? {
            count: interopSyncableItems.count,
            isPending: isSyncingDocuments,
            onClick: handleInteropSendClick,
          }
        : undefined,
    [
      handleInteropSendClick,
      interopSyncableItems.count,
      isInteropUser,
      isSyncingDocuments,
      medicalEvent,
    ],
  )

  const handleSelectItems = useCallback(
    ({
      documents,
      files,
    }: {
      documents: readonly DocumentInstanceListItem[]
      files: readonly CustomFile[]
    }) => {
      const selection = { documents: [...documents], files: [...files] }

      switch (isOpenSearchMedicalEventDocumentsBottomPanel) {
        case 'download':
          return handleDownload(selection)
        case 'interop':
          return handleSyncDocumentsToInterop(selection)
        default:
          return handlePrint(selection)
      }
    },
    [
      handleDownload,
      handlePrint,
      handleSyncDocumentsToInterop,
      isOpenSearchMedicalEventDocumentsBottomPanel,
    ],
  )
  const handleSendMail = useCallback(() => {
    testMailConnection(undefined, {
      onSuccess: (data) => {
        if (data) {
          setShowSendFileModal(true)
        }
      },
    })
  }, [testMailConnection])

  if (!patient) {
    return null
  }

  const handleSubmitEvent = async (
    includeObservations: boolean,
    addresses: Array<string>,
    documents?: Array<SendableDocument>,
    fileIds?: Array<number>,
  ) => {
    const object = !!documents?.length
      ? await getMailSubject({ documentIds: documents.map(({ id }) => id) })
      : undefined

    setInitialMail({
      content: includeObservations ? medicalEvent?.observation : undefined,
      emails: addresses,
      documents,
      object,
      fileIds,
    })
  }

  return (
    <>
      <div className={styles.contentWrapper}>
        <PatientPageHeader patient={patient} hideInteropActions={isDefined(medicalEvent)} />
        {medicalEvent && (
          <div className={styles.medicalEventHeader}>
            {!isOwner && (
              <>
                <div className={styles.ownerFullName}>{getFullName(medicalEvent.owner)}</div>
                <Space>{`•`}</Space>
              </>
            )}
            <div className={styles.medicalEventTitle}>
              {`${medicalEvent.title}, `}
              {formatFr(new Date(medicalEvent.date), DATE_FORMAT_FRONT_SHORT)}
            </div>
            <Space />
            {medicalEvent.isEditable && (
              <div className={styles.action}>
                <IconButton
                  onClick={() => editMedicalEvent()}
                  icon="pencil"
                  theme="dark"
                  testId="button-medical_event-edit"
                />
              </div>
            )}
            {allowedToLock && (
              <div className={styles.action}>
                <IconButton
                  onClick={() => onLock(!medicalEvent.locked)}
                  title={
                    medicalEvent.locked ? 'Cliquer pour déverrouiller' : 'Cliquer pour verrouiller'
                  }
                  icon={medicalEvent.locked ? 'lock' : 'unlock'}
                  theme="dark"
                  testId={`button-medical_event-${medicalEvent.locked ? 'unlock' : 'lock'}`}
                />
              </div>
            )}
            <div className={styles.action}>
              <Tooltip
                label={isPrinting ? 'Impression en cours...' : 'Imprimer des fichiers et documents'}
              >
                <IconButton
                  onClick={() => setIsOpenSearchMedicalEventDocumentsBottomPanel('print')}
                  icon="print"
                  isLoading={isPrinting}
                  disabled={isPrinting}
                  theme="dark"
                  testId="button-medical_event-documents_to_print-select"
                />
              </Tooltip>
            </div>
            {userEnabledFeatures?.mail && (
              <div className={styles.action}>
                <TooltipWrapper
                  content={tooltipContent}
                  display={
                    !patient.inseeNumber ||
                    (patient.disallowProfessionalMSSMessaging &&
                      patient.disallowCitizenMSSMessaging) ||
                    (userEnabledFeatures.mssSmtp &&
                      isDoctor(currentUser) &&
                      !currentUser.mailizEmailAddress)
                  }
                  pointerDirection="right"
                >
                  {userEnabledFeatures.mssSmtp &&
                  isDoctor(currentUser) &&
                  !currentUser.mailizEmailAddress ? (
                    <div>
                      <IconButton
                        onClick={() => navigate('/account/settings?focus=mailizEmailAddress')}
                        icon="mail"
                        theme="dark"
                        testId="button-medical_event-settings-mailiz_email-add"
                      />
                    </div>
                  ) : (
                    <div>
                      <IconButton
                        onClick={handleSendMail}
                        icon="mail"
                        theme="dark"
                        disabled={
                          !patient.inseeNumber ||
                          (patient.disallowProfessionalMSSMessaging &&
                            patient.disallowCitizenMSSMessaging)
                        }
                        testId="button-medical_event-email-create"
                      />
                    </div>
                  )}
                </TooltipWrapper>
              </div>
            )}
            <div className={styles.action}>
              <Tooltip
                label={
                  isDownloading
                    ? 'Téléchargement en cours...'
                    : 'Télécharger des fichiers et documents'
                }
              >
                <IconButton
                  onClick={() => setIsOpenSearchMedicalEventDocumentsBottomPanel('download')}
                  icon="save"
                  isLoading={isDownloading}
                  disabled={isDownloading}
                  theme="primary"
                  testId="button-medical_event-documents-download"
                />
              </Tooltip>
            </div>
            {interopDocumentsAction && (
              <div className={styles.action}>
                <InteropActionsMenu patient={patient} documentsAction={interopDocumentsAction} />
              </div>
            )}
            <SearchMedicalEventDocumentsBottomPanel
              display={!!isOpenSearchMedicalEventDocumentsBottomPanel}
              onRequestClose={() => setIsOpenSearchMedicalEventDocumentsBottomPanel(false)}
              medicalEvent={medicalEvent}
              title={
                SELECTION_PANEL_TITLES[
                  getSelectionMode(isOpenSearchMedicalEventDocumentsBottomPanel)
                ]
              }
              onSelect={handleSelectItems}
            />
          </div>
        )}
      </div>
      <SendFileModal
        display={showSendFileModal || isMailSubjectLoading}
        onClose={() => setShowSendFileModal(false)}
        onSubmit={handleSubmitEvent}
        currentMedicalEvent={medicalEvent}
      />
      <MailEditor
        display={isDefined(initialMail)}
        onClose={() => setInitialMail(undefined)}
        medicalEventId={medicalEvent?.id}
        initialPatient={patient}
        initialContent={initialMail?.content}
        initialRecipients={initialMail?.emails}
        initialDocuments={initialMail?.documents}
        initialSubject={initialMail?.object}
        initialFileIds={initialMail?.fileIds}
      />
    </>
  )
}
